import { MutableRefObject } from 'react';
import snakecaseKeys from 'snakecase-keys';

import { IDeviceProperty } from '../interfaces/IDeviceProperty';
import { IHeaterModel } from '../interfaces/IHeaterModel';
import { IOdooRoom } from '../interfaces/IOdooRoomsResponse';
import IState from '../interfaces/IState';
import { JobType } from '../pages/Jobs';
import { TFirmwareUptodateness, TRequestParams } from '../types/Generic';
import { THomeAssetData } from '../types/HomeAssetData';
import ImageInfo from '../types/ImageInfo';
import { isCeeEnabled, isSocialHousing } from '../utils/cee';
import { API_URL } from '../utils/constants';
import { getDeepomaticPatchArray } from '../utils/deepomatic';
import fetchWrapper from '../utils/fetchWrapper';
import modifyImageExifData from '../utils/modifyImageExifData';
import {
    accessTokenAtom,
    deepomaticAtom,
    deepomaticWorkOrderIdAtom,
    devicesListAtom,
    enableFastModeRequest,
    jobIdAtom,
    organisationalUnitAtom,
    typeInstallationAtom,
} from './Atoms';
import { readAtom } from './JotaiCallbacks';
import { stateSchema } from './Schemas';

export const interventionStateRequest = async (
    accessToken: string,
    jobId: number,
    signal?: AbortSignal | undefined
) => {
    try {
        const response: { state: IState } = await fetchWrapper(
            `${API_URL}/interventions/${jobId}/states/last/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                signal,
            }
        );
        const validatedSchema = stateSchema.validate(response.state);
        return validatedSchema.value;
    } catch (e: any) {
        throw e;
    }
};

export const jobsListRequest = async (
    accessToken: string,
    signal?: AbortSignal | undefined,
    filter?: JobType | undefined
) => {
    try {
        return fetchWrapper(
            filter && filter === JobType.PAST
                ? `${API_URL}/interventions/?past_jobs=true`
                : `${API_URL}/interventions/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                signal,
            }
        );
    } catch (e) {
        throw e;
    }
};

export const jobDetailsRequest = async (
    accessToken: string,
    jobId: number,
    signal?: AbortSignal | undefined
) => {
    try {
        return fetchWrapper(`${API_URL}/interventions/${jobId}/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            signal,
        });
    } catch (e) {
        throw e;
    }
};

export const photosListRequest = async (
    accessToken: string,
    jobId: number,
    signal?: AbortSignal | undefined
) => {
    try {
        const photoList: string[] = await fetchWrapper(
            `${API_URL}/interventions/${jobId}/upload/image/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                signal,
            }
        );
        return photoList;
    } catch (e) {
        throw e;
    }
};

export const generalTermsRequest = async (
    accessToken: string,
    jobId: number,
    signal?: AbortSignal | undefined
) => {
    try {
        const response = await fetchWrapper(
            `${API_URL}/interventions/${jobId}/general-terms/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                signal,
            }
        );

        return response['gtCguAgreed'];
    } catch (e) {
        throw e;
    }
};

export const acceptGeneralTermsRequest = async (
    accessToken: string,
    jobId: number
) => {
    try {
        const response = await fetchWrapper(
            `${API_URL}/interventions/${jobId}/general-terms/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
            }
        );
        trackActionsRequest('sign_cgu');
        return response;
    } catch (e) {
        throw e;
    }
};

export const errorReasonRequest = (
    accessToken: string,
    jobId: number,
    errorCode: string,
    retainDevicesAtSite: boolean
) => {
    try {
        return fetchWrapper(
            `${API_URL}/interventions/${jobId}/error-reason/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
                body: JSON.stringify(
                    snakecaseKeys({
                        errorCode,
                        retainDevicesAtSite,
                    })
                ),
            }
        );
    } catch (e) {
        throw e;
    }
};

export const inspectRequest = async (
    accessToken: string,
    jobId: number,
    meteringPointNumber: string,
    hasSmartMeter: boolean,
    hasSmartMeterSealed: boolean
) => {
    try {
        const payload = snakecaseKeys({
            meteringPointNumber: meteringPointNumber,
            hasSmartMeter: hasSmartMeter,
            hasSmartMeterSealed: hasSmartMeterSealed,
        });

        await fetchWrapper(`${API_URL}/interventions/${jobId}/inspect/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(payload),
            method: 'PUT',
        });
        trackActionsRequest('inspect', payload);
    } catch (e) {
        throw e;
    }
};

export const createUpdateRoomRequest = async (
    accessToken: string,
    jobId: number,
    room: {
        name: string;
        roomTypeId: number;
        nbDboxs?: number;
        nbSensors?: number;
        size?: string;
        height?: string;
        open?: string;
        insulation?: string;
        glazing?: string;
        exposition?: string;
        interventionReasonCodes?: {
            code: string;
            reason: string;
        }[];
        installerComment?: string;
    },
    roomId?: number
) => {
    try {
        const payload = snakecaseKeys(room);
        const response: { room: IOdooRoom } = await fetchWrapper(
            roomId
                ? `${API_URL}/interventions/${jobId}/rooms/${roomId}/`
                : `${API_URL}/interventions/${jobId}/rooms/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: roomId ? 'PATCH' : 'POST',
                body: JSON.stringify(payload),
            }
        );
        trackActionsRequest(roomId ? 'update_room' : 'create_room', payload);
        return response.room;
    } catch (e) {
        throw e;
    }
};

export const deleteAllRoomsRequest = async (
    accessToken: string,
    jobId: number
) => {
    try {
        await fetchWrapper(`${API_URL}/interventions/${jobId}/rooms/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            method: 'DELETE',
        });
        trackActionsRequest('delete_all_rooms');
    } catch (e) {
        throw e;
    }
};

export const deleteRoomRequest = async (
    accessToken: string,
    jobId: number,
    roomId: number
) => {
    try {
        await fetchWrapper(
            `${API_URL}/interventions/${jobId}/rooms/${roomId}/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'DELETE',
            }
        );
        trackActionsRequest('delete_room', { roomId });
    } catch (e) {
        throw e;
    }
};

export const createDeviceRequest = async (
    accessToken: string,
    jobId: number,
    device: {
        roomId: number;
        macAddress: string;
    }
) => createUpdateDeviceRequest(accessToken, jobId, device);
export const updateDeviceRequest = async (
    accessToken: string,
    jobId: number,
    deviceId: number,
    device: {
        brand?: string;
        model?: string;
        nominalPowerKw?: string;
        screen?: boolean;
        setting?: string;
        thermostat?: number;
        location?: string;
        heaterLocation?: string;
        // heaterType?: string;
        underWindow?: string;
        wallSurface?: string;
        dboxHeaterRelativeLocation?: string;
        dboxAirFlowQuality?: string;
        dboxCloseToHeater?: string;
        holderPosition?: string;
        holderPoNumber?: string;
        holderProductionDate?: string;
        fallenDboxNanotapeOnWall?: string;
        fallenDboxNanotapeType?: string;
        fallenDboxNanotapeScrewHoles?: string;
    }
) => createUpdateDeviceRequest(accessToken, jobId, device, deviceId);
const createUpdateDeviceRequest = async (
    accessToken: string,
    jobId: number,
    device: {
        roomId?: number;
        macAddress?: string;
        brand?: string;
        model?: string;
        screen?: boolean;
        setting?: string;
    },
    deviceId?: number
) => {
    try {
        const payload = snakecaseKeys(device);
        const response: { device: THomeAssetData } = await fetchWrapper(
            deviceId
                ? `${API_URL}/interventions/${jobId}/devices/${deviceId}/`
                : `${API_URL}/interventions/${jobId}/devices/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: deviceId ? 'PATCH' : 'POST',
                body: JSON.stringify(payload),
            }
        );
        trackActionsRequest(
            deviceId ? 'update_device' : 'create_device',
            payload
        );
        return response.device;
    } catch (e) {
        throw e;
    }
};

export const deleteAllDevicesRequest = async (
    accessToken: string,
    jobId: number
) => {
    try {
        await fetchWrapper(`${API_URL}/interventions/${jobId}/devices/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            method: 'DELETE',
        });
        trackActionsRequest('delete_all_devices');
    } catch (e) {
        throw e;
    }
};

export const deleteDeviceRequest = async (
    accessToken: string,
    jobId: number,
    deviceId: number
) => {
    try {
        await fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/${deviceId}/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'DELETE',
            }
        );
        trackActionsRequest('delete_device', { deviceId });
    } catch (e) {
        throw e;
    }
};

export const devicesStateRequest = (
    accessToken: string,
    jobId: number,
    homeId: number,
    relativeAssetIds: number[]
) => {
    try {
        return fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/state/?homeId=${homeId}&relativeAssetIds=${relativeAssetIds.join(
                ','
            )}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
    } catch (e) {
        throw e;
    }
};

export const docRequest = (
    accessToken: string,
    jobId: number,
    params?: {
        installerComment?: string;
        customerComment?: string;
        needIntervention?: boolean;
        isRepairJustified?: boolean;
        hasSmartMeter?: boolean;
        hasSmartMeterSealed?: boolean;
        meteringPointNumber?: string;
        nbDboxTap?: number;
        nbRooms?: number;
        nbHeatings?: number;
        surface?: number;
        totalNumberHeating?: number;
        newPanelInstalled?: boolean;
        bathroomsHeatersWanted?: number;
        bathroomsHeatersInstalled?: number;
        elePowerMeter?: string;
        retainDevicesAtSite?: boolean;
        ceeHasInstallableBathroom?: boolean;
        ceeIsClientMainResidence?: boolean;
        ceeIsOlderThanTwoYears?: boolean;
        houseType?: string;
        pastIntervention?: boolean;
    }
) => {
    try {
        return fetchWrapper(`${API_URL}/interventions/${jobId}/doc/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            method: 'POST',
            body: JSON.stringify(
                snakecaseKeys({
                    ...params,
                })
            ),
        });
    } catch (e) {
        throw e;
    }
};

export const uploadImageRequest = async (
    accessToken: string,
    jobId: number,
    imageInfo: ImageInfo
) => {
    try {
        const organisationalUnit = await readAtom(organisationalUnitAtom);
        const typeInstallation = await readAtom(typeInstallationAtom);
        const hasCee = isCeeEnabled(
            typeInstallation,
            organisationalUnit[2].hasCee
        );
        const isSocialHousingCEE = isSocialHousing(organisationalUnit);
        const dataURL = imageInfo.dataUri;
        if (dataURL && (hasCee || isSocialHousingCEE)) {
            const dataURIWithExif = await modifyImageExifData(
                dataURL as string
            );
            imageInfo.dataUri = dataURIWithExif;
            /*  For debuging purpose this code will open new tab
                    with the edited image that can be download and
                    the exif can be checked                             */
            // if (dataURIWithExif) {
            //     var image = new Image();
            //     image.src = dataURIWithExif.toString();
            //     const w = window.open('');
            //     w?.document.write(image.outerHTML);
            // }
        }
        const payload = snakecaseKeys({
            ...imageInfo,
            hasCee: hasCee || isSocialHousingCEE,
        });
        const response = fetchWrapper(
            `${API_URL}/interventions/${jobId}/upload/image/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
                body: JSON.stringify(payload),
            }
        );
        trackActionsRequest('upload_image', {
            typepic: imageInfo.typepic,
            typefic: imageInfo.typefic,
        });
        return response;
    } catch (e) {
        throw e;
    }
};

export const getPoPForDevice = async (macAddress: string) => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);
        const response: IDeviceProperty = await fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/property/?macs=${macAddress}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
        return response[macAddress].bluetoothProofOfPossession;
    } catch (e) {
        throw e;
    }
};

export const getMeshStatusRequest = async () => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);
        const installationDevices: THomeAssetData[] =
            await readAtom(devicesListAtom);

        if (installationDevices.length === 0) {
            return {};
        }

        const homeId = +installationDevices[0].homeAssetId.split('/')[0];
        const relativeIds = installationDevices.map(
            (device) => +device.homeAssetId.split('/')[1]
        );

        await enableFastModeRequest(accessToken, jobId);

        return devicesStateRequest(accessToken, jobId, homeId, relativeIds);
    } catch (e) {
        throw e;
    }
};

export const getDevicesFirmwareStatusRequest = async (
    devices?: THomeAssetData[]
) => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);
        const installationDevices: THomeAssetData[] =
            devices || (await readAtom(devicesListAtom));

        if (installationDevices.length === 0) {
            return {};
        }

        const macs = installationDevices.map((device) => device.macAddress);
        return fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/firmware/?macs=${macs.join(
                ','
            )}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
    } catch (e) {
        throw e;
    }
};

const checkFirmwareStatusForDevices = async (devices: THomeAssetData[]) => {
    const firmwareStatus = await getDevicesFirmwareStatusRequest(devices);
    const updated = Object.values(firmwareStatus).every(
        (status) => status !== TFirmwareUptodateness.UPDATING
    );
    return updated;
};

let interval: ReturnType<typeof setInterval> | undefined = undefined;
export const pollFirmwareStatusForDevices = async (
    devices: THomeAssetData[]
) => {
    return new Promise((resolve, reject) => {
        let count = 0;
        interval && clearInterval(interval);
        interval = setInterval(async () => {
            count += 1;
            try {
                const updated = await checkFirmwareStatusForDevices(devices);
                if (updated || count >= 10) {
                    clearInterval(interval);
                    interval = undefined;
                    // Trigger an announce to update the firmware status
                    if (!updated) {
                        await triggerRequest(devices, { announce: true });
                    }
                    updated ? resolve(true) : reject('Firmware update failed');
                }
            } catch (e) {
                clearInterval(interval);
                reject(e);
            }
        }, 30000);
    });
};

export const updateDevicesFirmwareRequest = async (
    devices: THomeAssetData[]
) => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);
        const macs = devices.map((device) => device.macAddress);
        await fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/firmware/?update=true&macs=${macs.join(
                ','
            )}`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
        trackActionsRequest('update_firmware', {
            macs: macs.join(','),
        });
        return pollFirmwareStatusForDevices(devices);
    } catch (e) {
        throw e;
    }
};

export const triggerRequest = async (
    devices: THomeAssetData[],
    params?: TRequestParams
): Promise<void> => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);
        const macs = devices.map((device) => device.macAddress);
        await fetchWrapper(
            `${API_URL}/interventions/${jobId}/trigger-request/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
                body: JSON.stringify(
                    snakecaseKeys({
                        macs,
                        ...params,
                    })
                ),
            }
        );
    } catch (e) {
        throw e;
    }
};

export const trackActionsRequest = async (
    action: string,
    parameters: { [k: string]: any } = {}
) => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);
        const organisationalUnit = await readAtom(organisationalUnitAtom);

        return fetchWrapper(
            `${API_URL}/interventions/${jobId}/track-actions/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
                body: JSON.stringify(
                    snakecaseKeys({
                        action,
                        organisationalUnit: organisationalUnit[1],
                        parameters,
                    })
                ),
            }
        );
    } catch (e) {
        // We don't want to block the user if the tracking fails
        return {};
    }
};

export const closeInterventionRequest = async (doneError: boolean = false) => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);
        const deepomatic = readAtom(deepomaticAtom);
        const deepomaticWorkOrderId = await readAtom(
            deepomaticWorkOrderIdAtom
        );
        const deepomaticPatchArray = getDeepomaticPatchArray(deepomatic);
        await fetchWrapper(`${API_URL}/interventions/${jobId}/close/`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            method: 'POST',
            body: JSON.stringify(
                snakecaseKeys({
                    doneError,
                    deepomaticWorkOrderId,
                    deepomaticPatchArray,
                })
            ),
        });
        trackActionsRequest('close_intervention', {
            doneError,
        });
    } catch (e) {
        throw e;
    }
};

export const clearDevicesRequest = async () => {
    try {
        const jobId = readAtom(jobIdAtom);
        const accessToken = readAtom(accessTokenAtom);

        return fetchWrapper(
            `${API_URL}/interventions/${jobId}/devices/clear/`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                method: 'POST',
            }
        );
    } catch (e) {
        throw e;
    }
};

type HeaterBrandListRequestArgs = {
    accessToken: string;
    brand: string;
    signal?: AbortSignal | undefined;
};

const heaterBrandListRequest = async ({
    accessToken,
    brand,
    signal,
}: HeaterBrandListRequestArgs) => {
    try {
        let url = `${API_URL}/heaters/?brand=${brand}`;
        return fetchWrapper(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            signal,
        });
    } catch (e) {
        throw e;
    }
};

type GetHeaterBrandListArgs = {
    accessToken: string;
    brand: string;
    controllerRef: MutableRefObject<AbortController | undefined>;
};

export const getHeaterBrandList = async ({
    accessToken,
    brand,
    controllerRef,
}: GetHeaterBrandListArgs): Promise<string[]> => {
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const signal = controllerRef.current?.signal;
    const requestInputs = {
        accessToken: accessToken,
        brand: brand,
        signal: signal,
    };
    let brands;
    try {
        brands = await heaterBrandListRequest(requestInputs);
    } catch (e) {
        brands = [];
    }
    return brands;
};

type HeaterModelListRequestArgs = {
    accessToken: string;
    model: string;
    brand?: string;
    signal?: AbortSignal | undefined;
};

const heaterModelListRequest = async ({
    accessToken,
    model,
    brand,
    signal,
}: HeaterModelListRequestArgs) => {
    try {
        let url = `${API_URL}/heaters/?model=${model}`;
        if (brand) {
            url += `&brand=${brand}`;
        }
        return fetchWrapper(url, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            signal,
        });
    } catch (e) {
        throw e;
    }
};

type GetHeaterModelListArgs = {
    accessToken: string;
    model: string;
    brand?: string;
    controllerRef: MutableRefObject<AbortController | undefined>;
};

export const getHeaterModelList = async ({
    accessToken,
    brand,
    model,
    controllerRef,
}: GetHeaterModelListArgs): Promise<IHeaterModel[]> => {
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();
    const signal = controllerRef.current?.signal;
    const requestInputs = {
        accessToken: accessToken,
        brand: brand,
        signal: signal,
        model: model,
    };
    let models;
    try {
        models = await heaterModelListRequest(requestInputs);
    } catch (e) {
        models = [];
    }
    return models;
};
